import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/demo-data";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FolderKanban,
  Table2,
  Eye,
  ChevronLeft,
  ChevronRight,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Building2,
  UserPlus,
  Send,
  ClipboardList,
  Link as LinkIcon,
  AlertTriangle,
  CalendarClock,
  History,
  RefreshCw,
  ShieldAlert,
  Phone,
  Trophy,
  Ban,
} from "lucide-react";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { toast } from "sonner";

const OPS_STATUSES = [
  "NEW",
  "IN_REVIEW",
  "REQUESTED_DOCS",
  "WRITING",
  "READY",
  "SUBMITTED",
  "WON",
  "LOST",
  "CANCELLED",
] as const;

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  NEW: { label: "Nouveau", variant: "outline" },
  IN_REVIEW: { label: "En revue", variant: "secondary" },
  REQUESTED_DOCS: { label: "Attente docs", variant: "secondary" },
  WRITING: { label: "Rédaction", variant: "default" },
  READY: { label: "Prêt", variant: "default" },
  SUBMITTED: { label: "Soumis", variant: "default" },
  WON: { label: "Gagné", variant: "default" },
  LOST: { label: "Perdu", variant: "destructive" },
  CANCELLED: { label: "Annulé", variant: "destructive" },
};

const DOC_CATEGORY_LABELS: Record<string, string> = {
  KBIS: "Kbis",
  ASSURANCE: "Assurance RC Pro",
  URSSAF: "URSSAF",
  QUALIF: "Qualification",
  REFERENCE: "Référence",
  AUTRE: "Autre",
};

function isProjectAtRisk(project: any): string[] {
  const risks: string[] = [];
  const now = new Date();
  const closed = ["SUBMITTED", "WON", "LOST", "CANCELLED"];
  if (closed.includes(project.opsStatus || project.status)) return risks;

  if (project.Tender?.deadlineAt) {
    const deadline = new Date(project.Tender.deadlineAt);
    const daysLeft = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) risks.push("RETARD");
    else if (daysLeft <= 7) risks.push("URGENT");
  }
  if (project.internalDeadlineAt && new Date(project.internalDeadlineAt) < now) {
    risks.push("DEADLINE_INTERNE");
  }
  if (!project.PartnerAssignment) {
    risks.push("SANS_PARTENAIRE");
  }
  return risks;
}

function AtRiskCheckins() {
  const [page, setPage] = useState(1);
  const [handledIds, setHandledIds] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, refetch } = trpc.admin.projectCheckins.listAtRisk.useQuery({
    page,
    limit: 50,
    since: 7,
  });

  const toggleHandled = (id: string) => {
    setHandledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) return <ListPageSkeleton />;
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
        <AlertCircle className="h-12 w-12 text-destructive/40" />
        <p className="text-muted-foreground font-medium">Erreur de chargement</p>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />Réessayer
        </Button>
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="text-center py-12">
        <CheckCircle2 className="h-12 w-12 text-green-500/40 mx-auto mb-3" />
        <p className="text-muted-foreground font-medium">Aucun chantier à risque</p>
        <p className="text-sm text-muted-foreground mt-1">
          Tous les chantiers en cours ont un score satisfaisant
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        {data.total} alerte{data.total > 1 ? "s" : ""} — checkins score &le; 2 ou contact demandé (7 derniers jours)
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Statut</TableHead>
            <TableHead scope="col">Particulier</TableHead>
            <TableHead scope="col">Professionnel</TableHead>
            <TableHead scope="col">Projet</TableHead>
            <TableHead scope="col">Score</TableHead>
            <TableHead scope="col">Problèmes signalés</TableHead>
            <TableHead scope="col">Date</TableHead>
            <TableHead scope="col">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((item: any) => {
            const handled = handledIds.has(item.id);
            return (
              <TableRow key={item.id} className={handled ? "opacity-50" : ""}>
                <TableCell>
                  {handled ? (
                    <Badge variant="outline" className="text-xs">Traité</Badge>
                  ) : (
                    <Badge variant="destructive" className="text-xs">ALERTE</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium text-sm">{item.particulierName || "—"}</p>
                    <p className="text-xs text-muted-foreground">{item.particulierEmail}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{item.proCompanyName || "—"}</TableCell>
                <TableCell className="text-sm max-w-[200px] truncate">
                  {item.projectTitle || "—"}
                  {item.projectCity && (
                    <span className="text-xs text-muted-foreground ml-1">({item.projectCity})</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={item.score <= 1 ? "destructive" : "secondary"} className="text-sm font-bold">
                    {item.score}/5
                  </Badge>
                  {item.wantsContact && (
                    <Badge variant="outline" className="ml-1 text-xs border-orange-400 text-orange-600">
                      <Phone className="h-3 w-3 mr-0.5" />Contact
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="max-w-[250px]">
                  <p className="text-sm text-muted-foreground line-clamp-2">{item.concerns || "—"}</p>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatDate(item.submittedAt)}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {item.particulierEmail && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`mailto:${item.particulierEmail}?subject=Suivi de votre chantier`, "_blank")}
                      >
                        <Send className="h-3 w-3 mr-1" />Contacter
                      </Button>
                    )}
                    <Button
                      variant={handled ? "ghost" : "secondary"}
                      size="sm"
                      onClick={() => toggleHandled(item.id)}
                    >
                      {handled ? "Rouvrir" : "Traité"}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {data.total > 50 && (
        <div className="flex items-center justify-between border-t pt-4">
          <div className="text-sm text-muted-foreground">
            Page {page} sur {Math.ceil(data.total / 50)}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4 mr-1" />Précédent
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= Math.ceil(data.total / 50)}>
              Suivant<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<"kanban" | "table">("kanban");
  const [mainTab, setMainTab] = useState<"projets" | "at-risk">("projets");
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [partnerFilter, setPartnerFilter] = useState(false);
  const [partnerPickerOpen, setPartnerPickerOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [noteText, setNoteText] = useState("");
  const [internalDeadline, setInternalDeadline] = useState("");

  // Close project dialog
  const [closeDialog, setCloseDialog] = useState<{ open: boolean; projectId: string; projectTitle: string }>({ open: false, projectId: "", projectTitle: "" });
  const [closeOutcome, setCloseOutcome] = useState<"WON" | "LOST">("WON");
  const [closeAmount, setCloseAmount] = useState("");
  const [closeNotes, setCloseNotes] = useState("");

  const utils = trpc.useUtils();

  const { data, isLoading, isError, refetch } = trpc.admin.projects.list.useQuery({
    page,
    limit: 50,
    opsStatus: statusFilter || undefined,
    withoutPartner: partnerFilter || undefined,
  });

  const { data: projectDetail, isLoading: isLoadingDetail, isError: projectDetailError, refetch: refetchDetail } = trpc.admin.projects.get.useQuery(
    { id: selectedProject! },
    { enabled: !!selectedProject }
  );

  const { data: checklist, isLoading: isLoadingChecklist, refetch: refetchChecklist } = trpc.admin.projects.checklist.list.useQuery(
    { projectId: selectedProject! },
    { enabled: !!selectedProject }
  );

  const { data: messages, refetch: refetchMessages } = trpc.admin.projects.messages.list.useQuery(
    { projectId: selectedProject! },
    { enabled: !!selectedProject }
  );

  const { data: deliverables } = trpc.admin.projects.deliverables.list.useQuery(
    { projectId: selectedProject! },
    { enabled: !!selectedProject }
  );

  const { data: timeline, isLoading: timelineLoading } = trpc.admin.projects.timeline.useQuery(
    { projectId: selectedProject! },
    { enabled: !!selectedProject }
  );

  const { data: partners, isLoading: isLoadingPartners } = trpc.admin.partners.list.useQuery(
    { page: 1, limit: 50, active: true, search: partnerSearch || undefined },
    { enabled: partnerPickerOpen }
  );

  const updateMutation = trpc.admin.projects.update.useMutation({
    onSuccess: () => {
      toast.success("Projet mis à jour");
      refetch();
      if (selectedProject) refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  const assignPartnerMutation = trpc.admin.partners.assignToProject.useMutation({
    onSuccess: () => {
      toast.success("Partenaire assigné");
      setPartnerPickerOpen(false);
      refetch();
      if (selectedProject) refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  const initChecklistMutation = trpc.admin.projects.checklist.initStandard.useMutation({
    onSuccess: (result) => {
      toast.success(`Checklist initialisée (${result.created} éléments créés)`);
      refetchChecklist();
      if (selectedProject) refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  const addNoteMutation = trpc.admin.projects.messages.create.useMutation({
    onSuccess: () => {
      toast.success("Note ajoutée");
      setNoteText("");
      refetchMessages();
    },
    onError: (err) => toast.error(err.message),
  });

  const createInvoiceMutation = trpc.admin.projects.createInvoice.useMutation({
    onSuccess: () => {
      toast.success("Facture créée");
      if (selectedProject) refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  const setOutcomeMutation = trpc.admin.projects.setOutcome.useMutation({
    onSuccess: () => {
      toast.success(closeOutcome === "WON" ? "Projet gagné !" : "Projet marqué perdu");
      setCloseDialog({ open: false, projectId: "", projectTitle: "" });
      setCloseOutcome("WON");
      setCloseAmount("");
      setCloseNotes("");
      refetch();
      if (selectedProject) refetchDetail();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la clôture"),
  });

  const projectsByStatus = data?.items.reduce((acc: Record<string, any[]>, project: any) => {
    const status = project.opsStatus || "NEW";
    if (!acc[status]) acc[status] = [];
    acc[status].push(project);
    return acc;
  }, {} as Record<string, any[]>) || {};

  const handleStatusChange = (projectId: string, newStatus: string) => {
    updateMutation.mutate({ id: projectId, opsStatus: newStatus as any });
  };

  const handleSetInternalDeadline = (projectId: string) => {
    if (!internalDeadline) return;
    updateMutation.mutate({
      id: projectId,
      internalDeadlineAt: new Date(internalDeadline),
    });
  };

  const handleAddNote = (projectId: string) => {
    if (!noteText.trim()) return;
    addNoteMutation.mutate({
      projectId,
      message: noteText.trim(),
      authorType: "ADMIN",
    });
  };

  const handleAssignPartner = (partnerId: string) => {
    if (!selectedProject) return;
    assignPartnerMutation.mutate({
      partnerId,
      projectId: selectedProject,
    });
  };

  // Compute checklist stats from detail
  const checklistStats = (() => {
    const items = (projectDetail as any)?.ProjectChecklistItem || checklist || [];
    const total = items.length;
    const ok = items.filter((i: any) => i.status === "OK").length;
    const missing = total - ok;
    return { total, ok, missing };
  })();

  // Compute company doc expiry from detail
  const expiredDocs = (() => {
    const docs = (projectDetail as any)?.companyDocuments || [];
    const now = new Date();
    return docs.filter((d: any) => d.expiresAt && new Date(d.expiresAt) < now).length;
  })();

  const RiskBadges = ({ project }: { project: any }) => {
    const risks = isProjectAtRisk(project);
    if (risks.length === 0) return null;
    return (
      <div className="flex gap-1 flex-wrap">
        {risks.includes("RETARD") && <Badge variant="destructive" className="text-xs">Retard</Badge>}
        {risks.includes("URGENT") && <Badge variant="destructive" className="text-xs">Urgent</Badge>}
        {risks.includes("DEADLINE_INTERNE") && <Badge variant="destructive" className="text-xs">DL interne</Badge>}
        {risks.includes("SANS_PARTENAIRE") && <Badge variant="outline" className="text-xs border-warning text-warning">Sans partenaire</Badge>}
      </div>
    );
  };

  const atRiskQuery = trpc.admin.projectCheckins.listAtRisk.useQuery(
    { page: 1, limit: 1, since: 7 },
    { refetchOnWindowFocus: false }
  );
  const atRiskCount = atRiskQuery.data?.total ?? 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Projets</h1>
            <p className="text-muted-foreground">
              Piloter la production des réponses AO
              {data && ` (${data.total} projets)`}
            </p>
          </div>
          <div className="flex gap-2">
            {mainTab === "projets" && (
              <>
                <Button
                  variant={viewMode === "kanban" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("kanban")}
                >
                  <FolderKanban className="h-4 w-4 mr-2" />
                  Kanban
                </Button>
                <Button
                  variant={viewMode === "table" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                >
                  <Table2 className="h-4 w-4 mr-2" />
                  Table
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant={mainTab === "projets" ? "default" : "outline"}
            size="sm"
            onClick={() => setMainTab("projets")}
          >
            <FolderKanban className="h-4 w-4 mr-2" />
            Projets AO
          </Button>
          <Button
            variant={mainTab === "at-risk" ? "default" : "outline"}
            size="sm"
            onClick={() => setMainTab("at-risk")}
          >
            <ShieldAlert className="h-4 w-4 mr-2" />
            Chantiers à risque
            {atRiskCount > 0 && (
              <Badge variant="destructive" className="ml-2 text-xs px-1.5">{atRiskCount}</Badge>
            )}
          </Button>
        </div>

        {mainTab === "at-risk" ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-destructive" />
                Chantiers à risque
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AtRiskCheckins />
            </CardContent>
          </Card>
        ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <select
                className="px-3 py-2 border rounded-md"
                aria-label="Filtrer par statut"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="">Tous les statuts</option>
                {OPS_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={partnerFilter}
                  onChange={(e) => { setPartnerFilter(e.target.checked); setPage(1); }}
                />
                Sans partenaire
              </label>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <ListPageSkeleton />
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" />
                <p className="text-muted-foreground font-medium">Erreur de chargement des projets</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />Réessayer
                </Button>
              </div>
            ) : viewMode === "kanban" ? (
              <div className="flex gap-4 overflow-x-auto pb-4">
                {OPS_STATUSES.map((status) => {
                  const projects = projectsByStatus[status] || [];
                  return (
                    <div key={status} className="flex-shrink-0 w-80">
                      <div className="bg-muted p-3 rounded-t-lg font-semibold flex items-center justify-between">
                        <span>{STATUS_CONFIG[status]?.label || status}</span>
                        <Badge variant="outline">{projects.length}</Badge>
                      </div>
                      <div className="border border-t-0 rounded-b-lg p-2 space-y-2 min-h-[400px] bg-muted/20">
                        {projects.map((project: any) => {
                          const risks = isProjectAtRisk(project);
                          return (
                            <Card
                              key={project.id}
                              className={`cursor-pointer hover:shadow-md transition-shadow ${risks.includes("RETARD") ? "border-destructive/30" : ""}`}
                              onClick={() => setSelectedProject(project.id)}
                            >
                              <CardContent className="p-3">
                                <div className="space-y-1.5">
                                  <h4 className="font-medium text-sm line-clamp-2">
                                    {project.Tender?.title || "Projet sans AO"}
                                  </h4>
                                  <p className="text-xs text-muted-foreground">
                                    {project.Company?.name}
                                  </p>
                                  {project.Tender?.deadlineAt && (
                                    <p className="text-xs text-muted-foreground">
                                      <Clock className="h-3 w-3 inline mr-1" />
                                      {formatDate(project.Tender.deadlineAt)}
                                    </p>
                                  )}
                                  <div className="flex gap-1 flex-wrap">
                                    {project.PartnerAssignment?.Partner ? (
                                      <Badge variant="outline" className="text-xs">
                                        {project.PartnerAssignment.Partner.name}
                                      </Badge>
                                    ) : null}
                                    <RiskBadges project={project} />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                        {projects.length === 0 && (
                          <div className="text-center py-8">
                            <FolderKanban className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">Aucun projet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Entreprise</TableHead>
                    <TableHead scope="col">AO</TableHead>
                    <TableHead scope="col">Deadline</TableHead>
                    <TableHead scope="col">Statut</TableHead>
                    <TableHead scope="col">Partenaire</TableHead>
                    <TableHead scope="col">Risques</TableHead>
                    <TableHead scope="col">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.items.map((project: any) => (
                    <TableRow key={project.id} className="hover:bg-muted/50 transition-colors duration-200 cursor-pointer">
                      <TableCell className="font-medium">{project.Company?.name}</TableCell>
                      <TableCell className="max-w-xs truncate">{project.Tender?.title || "-"}</TableCell>
                      <TableCell>{project.Tender?.deadlineAt ? formatDate(project.Tender.deadlineAt) : "-"}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_CONFIG[project.opsStatus]?.variant || "outline"}>
                          {STATUS_CONFIG[project.opsStatus]?.label || project.opsStatus}
                        </Badge>
                      </TableCell>
                      <TableCell>{project.PartnerAssignment?.Partner?.name || <span className="text-warning text-xs">Non assigné</span>}</TableCell>
                      <TableCell><RiskBadges project={project} /></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" aria-label="Voir le projet" onClick={() => setSelectedProject(project.id)}>
                            <Eye className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          {canAct && !["WON", "LOST", "CANCELLED"].includes(project.opsStatus) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground"
                              aria-label="Clôturer le projet"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCloseDialog({ open: true, projectId: project.id, projectTitle: project.Tender?.title || project.Company?.name || "" });
                              }}
                            >
                              <Trophy className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!isLoading && data?.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12">
                        <FolderKanban className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-muted-foreground">Aucun projet en cours</p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}

            {data && data.total > 0 && viewMode === "table" && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {page} sur {Math.ceil(data.total / 50)}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="h-4 w-4 mr-1" />Précédent
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= Math.ceil((data?.total || 0) / 50)}>
                    Suivant<ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      {/* ==================== FICHE PROJET COMPLÈTE ==================== */}
      <Dialog open={!!selectedProject} onOpenChange={(open) => { if (!open) setSelectedProject(null); }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          {projectDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge variant={STATUS_CONFIG[(projectDetail as any).opsStatus]?.variant || "outline"} className="text-sm">
                    {STATUS_CONFIG[(projectDetail as any).opsStatus]?.label || (projectDetail as any).opsStatus}
                  </Badge>
                  {(projectDetail as any).Tender?.title || "Projet"}
                </DialogTitle>
                <DialogDescription className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {(projectDetail as any).Company?.name}
                  </span>
                  {(projectDetail as any).Tender?.deadlineAt && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Deadline: {formatDate((projectDetail as any).Tender.deadlineAt)}
                    </span>
                  )}
                  {(projectDetail as any).TenderMatch?.score && (
                    <Badge variant="outline">Score: {(projectDetail as any).TenderMatch.score}</Badge>
                  )}
                </DialogDescription>
              </DialogHeader>

              <Tabs defaultValue="resume" className="mt-4">
                <TabsList className="grid w-full grid-cols-6">
                  <TabsTrigger value="resume">Résumé</TabsTrigger>
                  <TabsTrigger value="documents">
                    Documents
                    {checklistStats.missing > 0 && (
                      <Badge variant="destructive" className="ml-1 text-xs px-1">{checklistStats.missing}</Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="notes">Notes ({(messages || []).length})</TabsTrigger>
                  <TabsTrigger value="timeline">Historique</TabsTrigger>
                  <TabsTrigger value="deliverables">Livrables ({(deliverables || []).length})</TabsTrigger>
                  <TabsTrigger value="partner">Partenaire</TabsTrigger>
                </TabsList>

                {/* ===== TAB: RÉSUMÉ ===== */}
                <TabsContent value="resume" className="space-y-4">
                  <div className="grid grid-cols-2 gap-6">
                    {/* Left: Projet */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Projet</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Statut OPS</span>
                            <select
                              className="px-2 py-1 border rounded text-sm"
                              value={(projectDetail as any).opsStatus || ""}
                              onChange={(e) => handleStatusChange((projectDetail as any).id, e.target.value)}
                            >
                              {OPS_STATUSES.map((s) => (
                                <option key={s} value={s}>{STATUS_CONFIG[s]?.label || s}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Deadline externe</span>
                            <span className="font-medium">
                              {(projectDetail as any).Tender?.deadlineAt
                                ? formatDate((projectDetail as any).Tender.deadlineAt)
                                : "-"}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Deadline interne</span>
                            <div className="flex items-center gap-2">
                              <input
                                type="date"
                                className="px-2 py-1 border rounded text-sm"
                                defaultValue={(projectDetail as any).internalDeadlineAt
                                  ? new Date((projectDetail as any).internalDeadlineAt).toISOString().split("T")[0]
                                  : ""}
                                onChange={(e) => setInternalDeadline(e.target.value)}
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSetInternalDeadline((projectDetail as any).id)}
                                disabled={!internalDeadline}
                              >
                                <CalendarClock className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Créé le</span>
                            <span>{formatDate((projectDetail as any).createdAt)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Risk indicators */}
                      {(() => {
                        const risks = isProjectAtRisk(projectDetail as any);
                        if (risks.length === 0) return null;
                        return (
                          <div className="p-3 border border-destructive/20 bg-destructive/5 rounded-lg">
                            <h5 className="font-medium text-destructive text-sm mb-1 flex items-center gap-1">
                              <AlertTriangle className="h-4 w-4" /> Alertes
                            </h5>
                            <div className="flex gap-1 flex-wrap">
                              {risks.map((r) => (
                                <Badge key={r} variant="destructive" className="text-xs">
                                  {r === "RETARD" && "En retard"}
                                  {r === "URGENT" && "Deadline < 7j"}
                                  {r === "DEADLINE_INTERNE" && "DL interne dépassée"}
                                  {r === "SANS_PARTENAIRE" && "Sans partenaire"}
                                </Badge>
                              ))}
                              {checklistStats.missing > 0 && (
                                <Badge variant="destructive" className="text-xs">{checklistStats.missing} doc(s) manquant(s)</Badge>
                              )}
                              {expiredDocs > 0 && (
                                <Badge variant="destructive" className="text-xs">{expiredDocs} doc(s) expiré(s)</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Stats row */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-2 border rounded">
                          <div className="text-lg font-bold">{checklistStats.ok}/{checklistStats.total}</div>
                          <p className="text-xs text-muted-foreground">Docs OK</p>
                        </div>
                        <div className="text-center p-2 border rounded">
                          <div className="text-lg font-bold">{(deliverables || []).length}</div>
                          <p className="text-xs text-muted-foreground">Livrables</p>
                        </div>
                        <div className="text-center p-2 border rounded">
                          <div className="text-lg font-bold">{(messages || []).length}</div>
                          <p className="text-xs text-muted-foreground">Notes</p>
                        </div>
                      </div>
                    </div>

                    {/* Right: Entreprise & AO */}
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-2">Entreprise</h4>
                        <div className="space-y-1 text-sm">
                          <p className="font-medium">{(projectDetail as any).Company?.name}</p>
                          {(projectDetail as any).Company?.email && (
                            <p className="text-muted-foreground">{(projectDetail as any).Company.email}</p>
                          )}
                          {(projectDetail as any).Company?.siret && (
                            <p className="text-muted-foreground">SIRET: {(projectDetail as any).Company.siret}</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Appel d'offres</h4>
                        <div className="space-y-1 text-sm">
                          <p className="font-medium">{(projectDetail as any).Tender?.title || "-"}</p>
                          <p className="text-muted-foreground">Acheteur: {(projectDetail as any).Tender?.buyer || "-"}</p>
                          <p className="text-muted-foreground">Lieu: {(projectDetail as any).Tender?.location || "-"}</p>
                          {((projectDetail as any).Tender?.budgetMin || (projectDetail as any).Tender?.budgetMax) && (
                            <p className="text-muted-foreground">
                              Budget: {(projectDetail as any).Tender?.budgetMin?.toLocaleString("fr-FR")}€
                              {(projectDetail as any).Tender?.budgetMax && ` - ${(projectDetail as any).Tender.budgetMax.toLocaleString("fr-FR")}€`}
                            </p>
                          )}
                          {(projectDetail as any).Tender?.source && (
                            <Badge variant="outline" className="text-xs">{(projectDetail as any).Tender.source}</Badge>
                          )}
                        </div>
                      </div>
                      {/* Partenaire résumé */}
                      <div>
                        <h4 className="font-semibold mb-2">Partenaire</h4>
                        {(projectDetail as any).PartnerAssignment?.Partner ? (
                          <div className="text-sm space-y-1">
                            <p className="font-medium">{(projectDetail as any).PartnerAssignment.Partner.name}</p>
                            <p className="text-muted-foreground">{(projectDetail as any).PartnerAssignment.Partner.email}</p>
                            <div className="flex gap-1 flex-wrap">
                              {(projectDetail as any).PartnerAssignment.Partner.zones?.slice(0, 3).map((z: string) => (
                                <Badge key={z} variant="outline" className="text-xs">{z}</Badge>
                              ))}
                            </div>
                            <Badge variant={(projectDetail as any).PartnerAssignment.status === "DELIVERED" ? "default" : "secondary"} className="text-xs">
                              {(projectDetail as any).PartnerAssignment.status}
                            </Badge>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm text-warning mb-2">Aucun partenaire assigné</p>
                            <Button size="sm" variant="outline" onClick={() => setPartnerPickerOpen(true)}>
                              <UserPlus className="h-3 w-3 mr-1" />Assigner
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 border-t pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Créer une facture d'assistance (490€) ?")) {
                          createInvoiceMutation.mutate({ projectId: (projectDetail as any).id, amount: 49000 });
                        }
                      }}
                    >
                      Créer facture (490€)
                    </Button>
                  </div>
                </TabsContent>

                {/* ===== TAB: DOCUMENTS / CHECKLIST ===== */}
                <TabsContent value="documents" className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Checklist documentaire</h4>
                      <p className="text-sm text-muted-foreground">
                        {checklistStats.ok}/{checklistStats.total} documents OK
                        {checklistStats.missing > 0 && ` • ${checklistStats.missing} manquant(s)`}
                        {expiredDocs > 0 && ` • ${expiredDocs} expiré(s)`}
                      </p>
                    </div>
                    {checklistStats.total === 0 && (
                      <Button
                        size="sm"
                        onClick={() => initChecklistMutation.mutate({ projectId: selectedProject! })}
                        disabled={initChecklistMutation.isPending}
                      >
                        <ClipboardList className="h-4 w-4 mr-1" />
                        Initialiser checklist BTP
                      </Button>
                    )}
                  </div>

                  {isLoadingChecklist ? (
                    <div className="space-y-2">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full rounded-lg" />
                      ))}
                    </div>
                  ) : checklist && checklist.length > 0 ? (
                    <div className="space-y-2">
                      {checklist.map((item: any) => {
                        // Cross-reference with company docs
                        const linkedDoc = item.docId
                          ? ((projectDetail as any)?.companyDocuments || []).find((d: any) => d.id === item.docId)
                          : null;
                        const isExpired = linkedDoc?.expiresAt && new Date(linkedDoc.expiresAt) < new Date();
                        const isExpiringSoon = linkedDoc?.expiresAt && !isExpired
                          && new Date(linkedDoc.expiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

                        return (
                          <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{item.label}</p>
                                {item.required && <Badge variant="outline" className="text-xs">Obligatoire</Badge>}
                              </div>
                              {linkedDoc && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  <LinkIcon className="h-3 w-3 inline mr-1" />
                                  {linkedDoc.name}
                                  {linkedDoc.expiresAt && (
                                    <span className={isExpired ? " text-destructive" : isExpiringSoon ? " text-warning" : ""}>
                                      {" "}(expire: {formatDate(linkedDoc.expiresAt)}{isExpired ? " - EXPIRÉ" : ""})
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>
                            <Badge variant={item.status === "OK" && !isExpired ? "default" : "destructive"}>
                              {item.status === "OK" && !isExpired ? (
                                <><CheckCircle2 className="h-3 w-3 mr-1" />OK</>
                              ) : isExpired ? (
                                <><AlertCircle className="h-3 w-3 mr-1" />Expiré</>
                              ) : (
                                <><XCircle className="h-3 w-3 mr-1" />Manquant</>
                              )}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ClipboardList className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Aucun élément de checklist</p>
                      <p className="text-sm text-muted-foreground">Cliquez sur "Initialiser checklist BTP" pour créer la liste standard</p>
                    </div>
                  )}

                  {/* Dossier socle entreprise */}
                  {(projectDetail as any)?.companyDocuments?.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-2">Dossier socle entreprise</h4>
                      <p className="text-xs text-muted-foreground mb-2">
                        Documents disponibles dans le dossier de {(projectDetail as any).Company?.name}
                      </p>
                      <div className="space-y-1">
                        {(projectDetail as any).companyDocuments.map((doc: any) => {
                          const isExpired = doc.expiresAt && new Date(doc.expiresAt) < new Date();
                          return (
                            <div key={doc.id} className="flex items-center justify-between p-2 border rounded text-sm">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span>{doc.name}</span>
                                <Badge variant="outline" className="text-xs">{DOC_CATEGORY_LABELS[doc.category] || doc.category}</Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                {doc.expiresAt && (
                                  <span className={`text-xs ${isExpired ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                                    {isExpired ? "Expiré" : formatDate(doc.expiresAt)}
                                  </span>
                                )}
                                {!isExpired && (
                                  <Badge variant="default" className="text-xs">Disponible</Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </TabsContent>

                {/* ===== TAB: NOTES INTERNES ===== */}
                <TabsContent value="notes" className="space-y-4">
                  {/* Add note form */}
                  <div className="flex gap-2">
                    <textarea
                      className="flex-1 min-h-[80px] px-3 py-2 border rounded-md text-sm"
                      placeholder="Ajouter une note interne..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                    />
                    <Button
                      size="sm"
                      onClick={() => handleAddNote(selectedProject!)}
                      disabled={!noteText.trim() || addNoteMutation.isPending}
                      className="self-end"
                    >
                      <Send className="h-4 w-4 mr-1" />
                      Envoyer
                    </Button>
                  </div>

                  {messages && messages.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {[...messages].reverse().map((msg: any) => (
                        <div key={msg.id} className={`p-3 border rounded-lg ${msg.authorType === "ADMIN" ? "border-l-4 border-l-primary" : ""}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <Badge variant={msg.authorType === "ADMIN" ? "default" : msg.authorType === "CLIENT" ? "secondary" : "outline"}>
                                {msg.authorType === "ADMIN" ? "OPS" : msg.authorType === "CLIENT" ? "Client" : "Partenaire"}
                              </Badge>
                              {msg.authorId && (
                                <span className="text-xs text-muted-foreground">{msg.authorId.slice(0, 8)}</span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">{formatDate(msg.createdAt)}</span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Send className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Aucune note</p>
                      <p className="text-sm text-muted-foreground">Ajoutez une note interne ci-dessus</p>
                    </div>
                  )}
                </TabsContent>

                {/* ===== TAB: HISTORIQUE / TIMELINE ===== */}
                <TabsContent value="timeline" className="space-y-2">
                  {timelineLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full rounded-lg" />
                      ))}
                    </div>
                  ) : timeline && timeline.length > 0 ? (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {timeline.map((log: any) => {
                        const TIMELINE_LABELS: Record<string, string> = {
                          PROJECT_UPDATE: "Projet mis à jour",
                          PROJECT_SET_OUTCOME: "Résultat défini",
                          CHECKLIST_INIT_STANDARD: "Checklist initialisée",
                          CHECKLIST_ITEM_CREATE: "Élément checklist ajouté",
                          DELIVERABLE_CREATE: "Livrable ajouté",
                          SERVICE_INVOICE_CREATE: "Facture créée",
                          PARTNER_ASSIGNED: "Partenaire assigné",
                        };

                        // Extract meaningful diff
                        const changes: string[] = [];
                        if (log.action === "PROJECT_UPDATE" && log.beforeJson && log.afterJson) {
                          if (log.beforeJson.opsStatus !== log.afterJson.opsStatus) {
                            changes.push(`Statut: ${log.beforeJson.opsStatus} → ${log.afterJson.opsStatus}`);
                          }
                          if (log.beforeJson.internalDeadlineAt !== log.afterJson.internalDeadlineAt) {
                            changes.push("Deadline interne modifiée");
                          }
                        }
                        if (log.action === "PROJECT_SET_OUTCOME" && log.afterJson) {
                          changes.push(`Issue: ${log.afterJson.outcome}`);
                          if (log.afterJson.awardedAmount) {
                            changes.push(`Montant: ${log.afterJson.awardedAmount.toLocaleString("fr-FR")}€`);
                          }
                        }

                        return (
                          <div key={log.id} className="flex gap-3 p-3 border rounded-lg">
                            <div className="flex-shrink-0 mt-0.5">
                              <History className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="font-medium text-sm">{TIMELINE_LABELS[log.action] || log.action}</p>
                                <span className="text-xs text-muted-foreground">{formatDate(log.createdAt)}</span>
                              </div>
                              {log.User && (
                                <p className="text-xs text-muted-foreground">
                                  Par {[log.User.firstName, log.User.lastName].filter(Boolean).join(" ") || log.User.email}
                                </p>
                              )}
                              {changes.length > 0 && (
                                <div className="mt-1 space-y-0.5">
                                  {changes.map((c, i) => (
                                    <p key={i} className="text-xs text-muted-foreground">{c}</p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <History className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground">Aucun historique</p>
                    </div>
                  )}
                </TabsContent>

                {/* ===== TAB: LIVRABLES ===== */}
                <TabsContent value="deliverables" className="space-y-2">
                  {deliverables && deliverables.length > 0 ? (
                    <div className="space-y-2">
                      {deliverables.map((del: any) => (
                        <div key={del.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            <div>
                              <span className="font-medium text-sm">{del.fileName || del.type}</span>
                              <Badge variant="outline" className="ml-2 text-xs">{del.type}</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{formatDate(del.createdAt)}</span>
                            <a href={del.fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                              Ouvrir
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">Aucun livrable</p>
                  )}
                </TabsContent>

                {/* ===== TAB: PARTENAIRE ===== */}
                <TabsContent value="partner" className="space-y-4">
                  {(projectDetail as any).PartnerAssignment?.Partner ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 text-sm">
                          <h4 className="font-semibold">Partenaire assigné</h4>
                          <p><strong>Nom:</strong> {(projectDetail as any).PartnerAssignment.Partner.name}</p>
                          <p><strong>Email:</strong> {(projectDetail as any).PartnerAssignment.Partner.email}</p>
                          {(projectDetail as any).PartnerAssignment.Partner.phone && (
                            <p><strong>Tél:</strong> {(projectDetail as any).PartnerAssignment.Partner.phone}</p>
                          )}
                          <p><strong>SLA:</strong> {(projectDetail as any).PartnerAssignment.Partner.slaHours}h</p>
                          <p><strong>Capacité:</strong> {(projectDetail as any).PartnerAssignment.Partner.dailyCapacity}/jour</p>
                          <Badge variant={(projectDetail as any).PartnerAssignment.Partner.active ? "default" : "destructive"}>
                            {(projectDetail as any).PartnerAssignment.Partner.active ? "Actif" : "Inactif"}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <h4 className="font-semibold">Assignment</h4>
                          <p><strong>Statut:</strong>
                            <Badge variant="outline" className="ml-2">{(projectDetail as any).PartnerAssignment.status}</Badge>
                          </p>
                          <p><strong>Assigné le:</strong> {formatDate((projectDetail as any).PartnerAssignment.assignedAt)}</p>
                          {(projectDetail as any).PartnerAssignment.internalDueDate && (
                            <p><strong>Deadline interne:</strong> {formatDate((projectDetail as any).PartnerAssignment.internalDueDate)}</p>
                          )}
                          {(projectDetail as any).PartnerAssignment.price && (
                            <p><strong>Prix:</strong> {(projectDetail as any).PartnerAssignment.price.toLocaleString("fr-FR")}€</p>
                          )}
                          <div className="pt-2">
                            <h5 className="font-medium mb-1">Zones</h5>
                            <div className="flex gap-1 flex-wrap">
                              {(projectDetail as any).PartnerAssignment.Partner.zones?.map((z: string) => (
                                <Badge key={z} variant="outline" className="text-xs">{z}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="pt-2">
                            <h5 className="font-medium mb-1">Compétences</h5>
                            <div className="flex gap-1 flex-wrap">
                              {(projectDetail as any).PartnerAssignment.Partner.skills?.map((s: string) => (
                                <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setPartnerPickerOpen(true)}>
                        Changer de partenaire
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <UserPlus className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-muted-foreground mb-3">Aucun partenaire assigné à ce projet</p>
                      <Button onClick={() => setPartnerPickerOpen(true)}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assigner un partenaire
                      </Button>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          ) : projectDetailError ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-4">
              <AlertCircle className="h-12 w-12 text-destructive/40" />
              <p className="text-muted-foreground font-medium">Erreur de chargement du projet</p>
              <Button variant="outline" onClick={() => refetchDetail()}>Réessayer</Button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-64" />
              </div>
              <div className="flex gap-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="grid grid-cols-6 gap-2 mt-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-9" />
                ))}
              </div>
              <div className="space-y-3 mt-4">
                <Skeleton className="h-36 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ==================== PARTNER PICKER MODAL ==================== */}
      <Dialog open={partnerPickerOpen} onOpenChange={setPartnerPickerOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assigner un partenaire</DialogTitle>
            <DialogDescription>Sélectionnez un partenaire actif</DialogDescription>
          </DialogHeader>
          <div className="mb-4">
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-md text-sm"
              placeholder="Rechercher par nom ou email..."
              value={partnerSearch}
              onChange={(e) => setPartnerSearch(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            {isLoadingPartners ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-lg" />
              ))
            ) : partners?.items.map((p: any) => (
              <div
                key={p.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent cursor-pointer"
                onClick={() => handleAssignPartner(p.id)}
              >
                <div>
                  <p className="font-medium text-sm">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.email}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {p.zones?.slice(0, 4).map((z: string) => (
                      <Badge key={z} variant="outline" className="text-xs">{z}</Badge>
                    ))}
                    {p.skills?.slice(0, 3).map((s: string) => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    ))}
                  </div>
                </div>
                <div className="text-right text-sm">
                  <p className="text-muted-foreground">{p.dailyCapacity}/j</p>
                  <p className="text-muted-foreground">{p.slaHours}h SLA</p>
                  <p className="text-xs">{p._count?.PartnerAssignment || 0} projet(s)</p>
                </div>
              </div>
            ))}
            {partners?.items.length === 0 && (
              <p className="text-center text-muted-foreground py-4">Aucun partenaire trouvé</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog clôture projet */}
      <Dialog open={closeDialog.open} onOpenChange={(open) => {
        if (!open) { setCloseDialog({ open: false, projectId: "", projectTitle: "" }); setCloseOutcome("WON"); setCloseAmount(""); setCloseNotes(""); }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Clôturer le projet</DialogTitle>
            <DialogDescription>
              {closeDialog.projectTitle?.substring(0, 60)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Verdict</Label>
              <Select value={closeOutcome} onValueChange={(v) => setCloseOutcome(v as "WON" | "LOST")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WON">
                    <span className="flex items-center gap-2"><Trophy className="h-3.5 w-3.5 text-success" /> Gagné</span>
                  </SelectItem>
                  <SelectItem value="LOST">
                    <span className="flex items-center gap-2"><Ban className="h-3.5 w-3.5 text-destructive" /> Perdu</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {closeOutcome === "WON" && (
              <div>
                <Label>Montant attribué (€)</Label>
                <Input
                  type="number"
                  placeholder="Ex : 50000"
                  value={closeAmount}
                  onChange={(e) => setCloseAmount(e.target.value)}
                />
              </div>
            )}
            <div>
              <Label>Commentaire (optionnel)</Label>
              <Textarea
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Notes sur la clôture…"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog({ open: false, projectId: "", projectTitle: "" })}>
              Annuler
            </Button>
            <Button
              variant={closeOutcome === "WON" ? "default" : "destructive"}
              onClick={() => {
                setOutcomeMutation.mutate({
                  projectId: closeDialog.projectId,
                  outcome: closeOutcome,
                  awardedAmount: closeOutcome === "WON" && closeAmount ? Number(closeAmount) : undefined,
                  notes: closeNotes || undefined,
                });
              }}
              disabled={setOutcomeMutation.isPending}
            >
              {setOutcomeMutation.isPending ? "Clôture…" : closeOutcome === "WON" ? "Marquer gagné" : "Marquer perdu"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
